import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheck, X, AlertCircle, History } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { LogoHorizontal } from '../components/Logo';
import { PrimaryButton } from '../components/Button';
import api, { formatApiError } from '../lib/api';

export function EmailVerified() {
    const navigate = useNavigate();
    return (
        <StatusScreen
            color="green"
            icon={CheckCheck}
            title="¡Email verificado!"
            subtitle="Tu cuenta ha sido activada exitosamente."
            badgeText="Ahora vamos a configurar tu perfil para que tu familia pueda identificarte fácilmente."
            primaryLabel="Continuar"
            onPrimary={() => navigate('/completar-perfil', { replace: true })}
            testId="email-verified-screen"
        />
    );
}

export function AccountNotVerified() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const email = params.get('email') || '';
    const [loading, setLoading] = React.useState(false);

    const resend = async () => {
        setLoading(true);
        try {
            await api.post('/auth/resend-code', { email });
            navigate(`/verificar-email?email=${encodeURIComponent(email)}`);
        } catch (e) {
            alert(formatApiError(e));
        } finally { setLoading(false); }
    };

    return (
        <StatusScreen
            color="orange"
            icon={AlertCircle}
            title="Cuenta no verificada"
            subtitle="Debes verificar tu correo electrónico antes de iniciar sesión."
            badgeText={`Enviamos un código de verificación a tu correo, si no lo encuentras o han pasado más de 24 horas puedes solicitar uno nuevo.`}
            primaryLabel={loading ? 'Enviando...' : 'Reenviar un código nuevo'}
            onPrimary={resend}
            secondaryLabel="Registrar con otro correo electrónico"
            onSecondary={() => navigate('/registro')}
            testId="account-not-verified-screen"
        />
    );
}

export function InvalidCode() {
    const navigate = useNavigate();
    return (
        <StatusScreen
            color="red"
            icon={X}
            title="Código de verificación inválido"
            subtitle="El código no coincide o probablemente habrá expirado."
            badgeText="Verifica el código e ingrésalo nuevamente. Si no funciona, puedes solicitar un código nuevo."
            primaryLabel="Reenviar un código nuevo"
            onPrimary={() => navigate(-2)}
            secondaryLabel="Registrar con otro correo electrónico"
            onSecondary={() => navigate('/registro')}
            testId="invalid-code-screen"
        />
    );
}

export function LinkExpired() {
    const navigate = useNavigate();
    return (
        <StatusScreen
            color="orange"
            icon={History}
            title="Enlace expirado"
            subtitle="Este enlace de verificación ha expirado."
            badgeText="Los enlaces de verificación expiran después de 24 horas por seguridad. Solicita un nuevo enlace para continuar."
            primaryLabel="Solicitar nuevo enlace"
            onPrimary={() => navigate(-2)}
            testId="link-expired-screen"
        />
    );
}

function StatusScreen({ color, icon: Icon, title, subtitle, badgeText, primaryLabel, onPrimary, secondaryLabel, onSecondary, testId }) {
    const palette = {
        green: { iconBg: 'bg-emerald-100', iconColor: 'text-emerald-500', badge: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
        red:   { iconBg: 'bg-red-100',     iconColor: 'text-red-500',     badge: 'bg-red-50 border-red-200 text-red-700' },
        orange:{ iconBg: 'bg-orange-100',  iconColor: 'text-orange-500',  badge: 'bg-orange-50 border-orange-200 text-orange-700' },
    }[color];
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-surface app-shell flex flex-col py-8 px-6" data-testid={testId}>
            <div className="flex justify-center mb-6"><LogoHorizontal size="lg" /></div>

            <div className="bg-white rounded-[24px] shadow-card p-6 fade-up">
                <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-5 ${palette.iconBg}`}>
                    <Icon className={`w-12 h-12 ${palette.iconColor}`} strokeWidth={2.5} />
                </div>
                <h2 className="text-2xl font-bold text-brand-navy text-center tracking-tight">{title}</h2>
                <p className="text-center text-gray-500 text-sm mt-2 leading-relaxed">{subtitle}</p>

                {badgeText && (
                    <div className={`mt-5 border-2 ${palette.badge} rounded-2xl p-4 text-sm text-center leading-relaxed font-medium`}>
                        {badgeText}
                    </div>
                )}

                <div className="mt-6">
                    <PrimaryButton onClick={onPrimary} testId={`${testId}-primary`}>{primaryLabel}</PrimaryButton>
                </div>
                {secondaryLabel && (
                    <button onClick={onSecondary} className="block mx-auto mt-4 text-brand-navy font-bold hover:underline text-sm" data-testid={`${testId}-secondary`}>
                        {secondaryLabel}
                    </button>
                )}
                <button onClick={() => navigate('/intro')} className="block mx-auto mt-4 text-gray-500 text-sm hover:text-gray-700" data-testid={`${testId}-back-home`}>
                    Volver al Inicio
                </button>
            </div>
        </div>
    );
}
